<script>
  import { createEventDispatcher, tick } from "svelte";
  import SelectInput from "./SelectInput.svelte";
  import TextInput from "./TextInput.svelte";
  import { liveStore } from "../Automator/store";

  export let value = { keyboard: "", text: "" };
  export let keyboardOptions = [];
  export let variant = "default";
  export let optional = false;
  export let isReadonly = false;

  const dispatch = createEventDispatcher();

  let keyboardData = null;
  let currentMode = "default";
  let parsedLayout = [];
  let isLoading = false;
  let previousKeyboard = null;
  let textInputContainer = null;

  // Escape sequence logic
  function shouldUseEscapeSequence(key) {
    if (key.length === 1 && key !== "<" && key !== ">") {
      return false;
    }
    return true;
  }

  async function scrollTextInputToEnd() {
    // Wait for DOM to update
    await tick();

    // Find the text input element and scroll it to the end
    if (textInputContainer) {
      const input = textInputContainer.querySelector('input[type="text"]');
      if (input) {
        input.scrollLeft = input.scrollWidth;
      }
    }
  }

  function appendKey(key) {
    if (isReadonly) return;

    // Check if this key triggers a mode transition
    if (keyboardData && keyboardData.transitions) {
      const transition = keyboardData.transitions.find(
        (t) => t.from === currentMode && t.with === key,
      );

      if (transition) {
        // Switch to the target mode instead of inserting text
        currentMode = transition.to;
        return;
      }
    }

    // No transition found, insert the key as text
    let textToAppend;

    if (key === "<") {
      textToAppend = "<<";
    } else if (key === ">") {
      textToAppend = ">>";
    } else if (shouldUseEscapeSequence(key)) {
      textToAppend = `<${key}>`;
    } else {
      textToAppend = key;
    }

    value = { ...value, text: value.text + textToAppend };
    dispatch("input", value);

    // Scroll to the end of the text input
    scrollTextInputToEnd();
  }

  function formatKeyLabel(key) {
    const specialLabels = {
      space: "space ␣",
      backspace: "backspace ⌫",
      enter: "enter ↵",
      submit: "submit ✓",
      shift: "shift ⇧",
      symbols: "symbols #+=",
      numbers: "numbers 123",
      delete: "delete ⌦",
      tab: "tab ⇥",
      return: "enter ↵",
    };
    return specialLabels[key.toLowerCase()] || key;
  }

  // Fetching & Parsing
  async function fetchKeyboardDetails(keyboardGroupValue) {
    if (!keyboardGroupValue || keyboardGroupValue === "default") {
      keyboardData = null;
      return;
    }

    isLoading = true;
    try {
      const result = await $liveStore.pushEvent("request_keyboard_details", {
        payload: { keyboard_group_value: keyboardGroupValue },
      });

      if (result.keyboard) {
        keyboardData = result.keyboard;
        currentMode = result.keyboard.initial_mode;
        updateParsedLayout();
      } else {
        keyboardData = null;
      }
    } catch (error) {
      console.error("Failed to fetch keyboard details:", error);
      keyboardData = null;
    } finally {
      isLoading = false;
    }
  }

  function updateParsedLayout() {
    if (!keyboardData) {
      parsedLayout = [];
      return;
    }

    const currentLayout = keyboardData.layouts.find((l) => l.name === currentMode);
    if (!currentLayout) {
      parsedLayout = [];
      return;
    }

    parsedLayout = parseLayout(currentLayout.layout);
  }

  function parseLayout(layoutString) {
    return layoutString
      .trim()
      .split("\n")
      .map((row) =>
        row
          .split(" ")
          .filter((k) => k !== "")
          .map(parseKey),
      );
  }

  function parseKey(key) {
    if (key.startsWith(":") && key !== ":") {
      const trimmed = key.replace(/^:+|:+$/g, "");
      const span = key.match(/^:+/)?.[0]?.length || 1;
      return {
        key: trimmed || key,
        span,
        isSpan: true,
        isEmpty: !trimmed,
      };
    }
    return {
      key: key === "nil" ? null : key,
      span: 1,
      isSpan: false,
      isEmpty: key === "nil",
    };
  }

  // Handle keyboard changes
  function handleKeyboardChange(event) {
    value = { ...value, keyboard: event.detail };
    dispatch("input", value);
  }

  // Handle text changes
  function handleTextChange(event) {
    value = { ...value, text: event.detail };
    dispatch("input", value);
  }

  // Reactive updates - only fetch when keyboard actually changes
  $: if (value.keyboard !== previousKeyboard) {
    previousKeyboard = value.keyboard;
    fetchKeyboardDetails(value.keyboard);
  }

  $: if (currentMode && keyboardData) {
    updateParsedLayout();
  }

  $: showKeyboard = value.keyboard && value.keyboard !== "default" && keyboardData !== null;
</script>

<div class="enter-text-input space-y-2" bind:this={textInputContainer}>
  <!-- Keyboard Selection -->
  <SelectInput
    bind:value={value.keyboard}
    options={keyboardOptions}
    label="Keyboard"
    {variant}
    {optional}
    showTooltip
    {isReadonly}
    on:input={handleKeyboardChange}
  />

  <!-- Text Input -->
  <TextInput
    bind:value={value.text}
    label="Text"
    placeholder="Type or click keys below"
    {variant}
    optional={false}
    {isReadonly}
    on:input={handleTextChange}
  />

  {#if showKeyboard}
    <!-- Mode Selector -->
    {#if keyboardData.layouts.length > 1}
      <div class="mode-selector">
        <label for="keyboard-mode-selector" class="text-xs font-medium text-content">Mode</label>
        <select
          id="keyboard-mode-selector"
          bind:value={currentMode}
          disabled={isReadonly}
          class="mt-1 block w-full rounded-md border-line-strong shadow-sm text-xs py-1 px-2"
        >
          {#each keyboardData.layouts as mode}
            <option value={mode.name}>
              {mode.name.charAt(0).toUpperCase() + mode.name.slice(1)}
            </option>
          {/each}
        </select>
      </div>
    {/if}

    <!-- Keyboard Visualization -->
    <div class="keyboard-visualization mt-3 overflow-x-auto">
      {#if isLoading}
        <div class="text-center text-content-subtle py-4 text-xs">Loading keyboard...</div>
      {:else if parsedLayout.length > 0}
        <div class="keyboard-grid space-y-1 inline-block">
          {#each parsedLayout as row}
            <div class="keyboard-row flex gap-1 whitespace-nowrap">
              {#each row as keyObj}
                {#if keyObj.isEmpty}
                  <div class="empty" style="width: {keyObj.span * 2.5}rem;"></div>
                {:else}
                  <button
                    type="button"
                    class="keyboard-key bg-surface border border-line-strong rounded px-2 py-1.5
                           hover:bg-surface-hover active:bg-surface-muted disabled:opacity-50
                           disabled:cursor-not-allowed text-xs font-medium flex-shrink-0"
                    style={keyObj.span > 1 ? `width: ${keyObj.span * 2.5}rem` : "min-width: 2.5rem"}
                    on:click={() => appendKey(keyObj.key)}
                    disabled={isReadonly}
                    title={keyObj.key}
                  >
                    {formatKeyLabel(keyObj.key)}
                  </button>
                {/if}
              {/each}
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>
